import PubSub from 'pubsub-js';
import { enqueueSnackbar } from 'design-system';
import axios, { Method, AxiosInstance } from 'axios';

import { getDeviceId } from 'src/utils';
import axiosInstance from 'src/services/axios';
import { STORAGE_KEY } from 'src/auth/context/jwt/auth-provider';

import { EventTopic } from './pubSub';

export type UnprocessableError<FormSchema> = {
  field: keyof FormSchema;
  message: string;
};
export enum HttpStatus {
  Ok = 200,
  Created = 201,
  Accepted = 202,
  NoContent = 204,
  BadRequest = 400,
  Unauthorized = 401,
  Forbidden = 403,
  Conflict = 409,
  NotFound = 404,
  Unprocessable = 422,
  Internal = 500,
}
export interface Header {
  [key: string]: string;
}

export interface QueryParams {
  [key: string]: string;
}

export interface RequestBody {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

export interface URLSearchParams {
  [key: string]: unknown;
}

export interface HttpClientParams {
  method: Method;
  url: string;
  headers?: Header;
  queryParams?: QueryParams;
  data?: RequestBody;
}
export interface ApiError {
  status: HttpStatus;
  message: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error: any;
}
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const apiErrorHandler = (error: any): ApiError => {
  try {
    return {
      status: error.response.status || error.response.data.status,
      message: error.response.message || error.response.data.message,
      error: error.response.error || error.response.data.error,
    };
  } catch (e) {
    return {
      status: HttpStatus.Internal,
      message: 'Unknown Error',
      error: 'Unknown Error',
    };
  }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const authApiErrorHandler = (error: any, logout: () => void): ApiError => {
  try {
    const status = error.status || error.response.status || error.response.data.status;
    if (status === HttpStatus.Unauthorized) {
      logout();
    }
    return {
      status,
      message: error.message || error.response?.message || error.response?.data?.message,
      error: error.error || error.response?.error || error.response?.data?.error,
    };
  } catch (e) {
    return {
      status: HttpStatus.Internal,
      message: 'Unknown Error',
      error: 'Unknown Error',
    };
  }
};

class HttpClient {
  private instance: AxiosInstance;

  constructor() {
    this.instance = axiosInstance;
  }

  init() {
    this.instance.interceptors.request.use((config) => {
      const token = localStorage.getItem(STORAGE_KEY);
      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      if (config.headers) {
        config.headers['X-Device-Id'] = getDeviceId();
      }
      return config;
    });
    this.instance.interceptors.response.use(
      (response) => response,
      (e) => {
        if (e?.type && e?.type === 'NetworkError') {
          enqueueSnackbar('当前网络信号弱', { variant: 'warning' });
        } else {
          PubSub.publish(EventTopic.API_ERRORS, e);
        }
        return Promise.reject(e);
      }
    );
  }

  async sendRequest(params: HttpClientParams) {
    const { method, url, headers, queryParams, data } = params;
    const response = await this.instance.request({
      url: `${url}`,
      method,
      headers,
      params: queryParams,
      data: data || undefined,
    });

    return response;
  }

  // eslint-disable-next-line class-methods-use-this
  async upload(url: string, data: File) {
    try {
      const formData = new FormData();
      formData.append('file', data);
      const config = {
        url: `${url}`,
        method: 'PUT',
        data,
      };
      const response = await axios.request(config);
      return response.data;
    } catch (e) {
      PubSub.publish(EventTopic.API_ERRORS, e);
      throw e;
    }
  }
}

const httpClient = new HttpClient();
httpClient.init();
export { httpClient };
